import { useEffect, useMemo, useRef, useState } from "react";
import TagManager from "react-gtm-module";
import { Interpolation, Theme, css } from "@emotion/react";
import styled from "@emotion/styled";
import { RecommendContentCard } from "components/cards/RecommendContentCard";
import { RecommendOrderBadge } from "components/cards/components/RecommendOrderBadge";
import { SectionScaffold } from "components/scaffolds/SectionScaffold";
import { useMixContentQuery } from "graphql/generated";

const SlideTrack = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  column-gap: 16px;
  border-radius: 12px;
  overflow: auto;

  ::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 1024px) {
    width: calc(100% + 2 * var(--page-padding));
    padding: 0 calc(var(--page-padding));
    margin-left: calc(-1 * var(--page-padding));
    scroll-padding: var(--page-padding);
    scroll-snap-type: x mandatory;
    border-radius: 0;
  }

  @media (max-width: 768px) {
    column-gap: 12px;
  }
`;

const RecommendContentCardWrap = styled.div`
  position: relative;
  min-width: 75%;
  height: 442px;
  border-radius: 12px;

  @media (max-width: 1024px) {
    min-width: 680px;
    height: 387px;
    flex: 1;
    scroll-snap-align: start;
  }

  @media (max-width: 768px) {
    min-width: 320px;
    height: 270px;
  }
`;

export const RecommendSection = ({
  contentSection,
  variables,
  limit = 2,
  ...props
}: {
  contentSection: ContentSection;
  variables: MixContentQueryVariables;
  limit?: number;
} & { css?: Interpolation<Theme> }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [focusedOrder, setFocusedOrder] = useState(0);
  const [pauseTimer, setPauseTimer] = useState(false);

  const { data } = useMixContentQuery({
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    variables: {
      ...variables,
      limit,
      offset: 0,
    },
  });

  const contents = useMemo(() => (data ? data.mix_content : []), [data]);

  // 타이머 재개
  useEffect(() => {
    if (!pauseTimer) {
      const interval = setInterval(() => {
        setFocusedOrder((focusedOrder + 1) % contents.length);
      }, 8000);

      return () => clearInterval(interval);
    }
  }, [focusedOrder, contents.length, pauseTimer]);

  // 포커스된 요소로 스크롤 이동
  useEffect(() => {
    const focusedChild = ref.current?.children[focusedOrder];
    if (!focusedChild || !(focusedChild instanceof HTMLElement)) return;

    ref.current.scrollTo({
      left: focusedChild.offsetLeft,
      behavior: "smooth",
    });
  }, [focusedOrder]);

  return (
    <SectionScaffold {...props}>
      <SectionScaffold.Header>
        <SectionScaffold.Column>
          <SectionScaffold.Title>데일리 큐레이션</SectionScaffold.Title>
          <SectionScaffold.Desc
            css={css`
              @media (max-width: 768px) {
                display: none;
              }
            `}
          >
            오늘 꼭 읽어봐야 할 추천 콘텐츠
          </SectionScaffold.Desc>
        </SectionScaffold.Column>
      </SectionScaffold.Header>
      <SectionScaffold.Contents>
        <SlideTrack
          ref={ref}
          onMouseEnter={() => setPauseTimer(true)}
          onMouseLeave={() => setPauseTimer(false)}
        >
          {contents.map((content) => (
            <RecommendContentCardWrap
              key={content.id}
              onMouseEnter={() =>
                typeof content.recommendorder === "number" &&
                setFocusedOrder(content.recommendorder)
              }
              onClick={() => {
                const params = {
                  content_section: contentSection,
                  content_url: content.originalurl,
                  content_isoriginal: content.isoriginal,
                  content_released: content.released,
                };

                TagManager.dataLayer({
                  dataLayer: {
                    event: "content_click",
                    ...params,
                    content_id: content.id,
                    content_title: content.title,
                  },
                });

                window.fbq("track", "ViewContent", {
                  ...params,
                  content_ids: [content.id],
                  content_name: content.title,
                  content_type: "product",
                });
              }}
            >
              {typeof content.recommendorder === "number" && (
                <RecommendOrderBadge
                  css={css`
                    position: sticky;
                    top: 44px;
                    left: 0;
                    display: block;
                    z-index: 1;

                    @media (max-width: 1024px) {
                      display: none;
                    }
                  `}
                >
                  {content.recommendorder + 1}
                </RecommendOrderBadge>
              )}
              <RecommendContentCard
                css={css`
                  position: absolute;
                  top: 0;
                `}
                content={content}
                focused={focusedOrder === content.recommendorder}
              />
            </RecommendContentCardWrap>
          ))}
        </SlideTrack>
      </SectionScaffold.Contents>
    </SectionScaffold>
  );
};
